import { cn, Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@newfront-insurance/core-ui/v2';
import { ArrowRight, Folder, FolderArchive, Loader2, X } from 'lucide-react';

import { novaGradientClass } from '../design-system/gradients';
import type { FolderApplyPlan, FolderApplyStatus } from '../lib/folder-artifact-apply';

function getFolderDisplayName(filename: string): string {
  return filename.replace(/\.zip$/i, '').trim() || filename;
}

const statusDisplay: Record<FolderApplyStatus, { action: string; result: string; className: string }> = {
  create: { action: 'Created', result: 'New file', className: 'text-grass-400' },
  overwrite: { action: 'Edited', result: 'File contents changed', className: 'text-fire-400' },
  conflict: { action: 'Conflict', result: 'Name conflict', className: 'text-fire-500' },
};

const planRowGridClassName =
  'grid h-10 grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)_minmax(64px,0.75fr)] items-center sm:grid-cols-[minmax(0,154px)_35px_minmax(0,154px)_minmax(0,1fr)]';

function FolderApplyDialog({
  folderName,
  isApplying,
  onCancel,
  onConfirm,
  plan,
}: {
  folderName: string;
  isApplying?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  plan: FolderApplyPlan | null;
}) {
  const hasConflicts = Boolean(plan?.conflictCount);
  const hasNoChanges = plan?.entries.length === 0;

  return (
    <Dialog open={Boolean(plan)} onOpenChange={(open) => !open && !isApplying && onCancel?.()}>
      <DialogContent
        showCloseButton={false}
        className="border-steel-200 w-[573px] max-w-[calc(100%-2rem)] gap-0 overflow-hidden rounded-[4px] p-0 font-sans"
      >
        <DialogHeader className="border-steel-200 flex h-[72px] flex-row items-center justify-between border-b px-6 py-[10px] text-left">
          <DialogTitle className="text-wtw-graymatter-800 min-w-0 truncate text-[17px] leading-6 font-normal">
            Confirm changes to {folderName}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              disabled={isApplying}
              className="text-wtw-graymatter-500 hover:text-wtw-graymatter-800 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close"
            >
              <X aria-hidden="true" className="size-[18px]" />
            </button>
          </DialogClose>
        </DialogHeader>
        {plan ? (
          <div className="flex h-[323px] flex-col gap-6 overflow-hidden px-6 py-8">
            <p className="text-wtw-graymatter-800 text-[14px] leading-5 font-semibold">Write changes to folder?</p>

            <div className="border-steel-200 w-[520px] max-w-full overflow-hidden rounded-[4px] border bg-white">
              <div className={planRowGridClassName}>
                <span className="text-steel-500 truncate px-[10px] text-[14px] leading-5 font-semibold tracking-[0.14px]">
                  Item
                </span>
                <ArrowRight aria-hidden="true" className="text-steel-200 mx-auto size-[15px]" />
                <span className="text-steel-500 truncate px-[10px] text-[14px] leading-5 font-semibold tracking-[0.14px]">
                  Result
                </span>
                <span className="text-steel-500 truncate px-[10px] text-right text-[14px] leading-5 font-semibold tracking-[0.14px]">
                  Action
                </span>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {hasNoChanges ? (
                  <div className="bg-steel-100 text-steel-400 flex h-10 items-center px-[10px] text-[14px] leading-5">
                    No changes to save.
                  </div>
                ) : (
                  plan.entries.map((entry, index) => (
                    <div
                      key={entry.path}
                      className={cn(planRowGridClassName, index % 2 === 0 ? 'bg-steel-100' : 'bg-white')}
                    >
                      <span className="text-steel-400 truncate px-[10px] text-[14px] leading-5 tracking-[0.14px]">
                        {entry.path}
                      </span>
                      <ArrowRight aria-hidden="true" className="text-steel-200 mx-auto size-[15px]" />
                      <span className="text-steel-500 truncate px-[10px] text-[14px] leading-5 tracking-[0.14px]">
                        {statusDisplay[entry.status].result}
                      </span>
                      <span
                        className={cn(
                          'truncate px-[10px] text-right text-[12px] leading-4 font-semibold',
                          statusDisplay[entry.status].className,
                        )}
                      >
                        {statusDisplay[entry.status].action}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}
        <div className="border-steel-200 flex h-[72px] items-center justify-end gap-[10px] border-t px-6 py-[10px]">
          <button
            type="button"
            onClick={onCancel}
            disabled={isApplying}
            className={cn(
              'border-steel-200 text-steel-500 h-10 rounded-[4px] border bg-white px-8',
              'hover:bg-steel-50 text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isApplying || hasConflicts || hasNoChanges}
            className={cn(
              'bg-brand-400 flex h-10 min-w-[95px] items-center justify-center gap-2 rounded-[4px] px-8',
              'hover:bg-brand-300 text-[14px] leading-5 font-semibold text-white',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {isApplying ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FolderArtifactCard({
  applyPlan,
  filename,
  isApplying = false,
  isApplySupported = false,
  onDownload,
  onApply,
  onCancelApply,
  onConfirmApply,
  isDownloading = false,
  isPreparingApply = false,
}: {
  applyPlan?: FolderApplyPlan | null;
  filename: string;
  isApplying?: boolean;
  isApplySupported?: boolean;
  onDownload?: () => void;
  onApply?: () => void;
  onCancelApply?: () => void;
  onConfirmApply?: () => void;
  isDownloading?: boolean;
  isPreparingApply?: boolean;
}) {
  const folderName = getFolderDisplayName(filename);
  const canApply = isApplySupported && Boolean(onApply);

  return (
    <div className="flex flex-col gap-3">
      <div className="px-[10px]">
        <div className={cn('rounded-[12px] p-[2px]', novaGradientClass.aiBorderIcon)}>
          <div className="bg-wtw-infinity-10 overflow-hidden rounded-[10px] font-sans">
            {canApply ? (
              <div className="flex min-h-[70px] items-center justify-between gap-4 px-5 py-4">
                <div className="flex min-w-0 items-center gap-4">
                  <Folder aria-hidden="true" className="text-steel-400 size-6 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-steel-500 text-[14px] leading-5 font-semibold">Save files to origin folder</p>
                    <p className="text-steel-400 mt-1 text-[12px] leading-4">
                      Nova writes the files straight in to a folder you select — no zip.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onApply}
                  disabled={isPreparingApply || isApplying}
                  className={cn(
                    'text-wtw-ultraviolet-850 flex h-9 shrink-0 items-center justify-center gap-[10px] rounded-full',
                    'px-4 text-[14px] leading-5 font-semibold transition-opacity hover:opacity-90',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    novaGradientClass.aiBackground,
                  )}
                  aria-label={`Save ${folderName} to folder`}
                >
                  {isPreparingApply ? (
                    <Loader2 aria-hidden="true" className="size-[18px] animate-spin" />
                  ) : (
                    <ArrowRight aria-hidden="true" className="size-[18px]" />
                  )}
                  Save to folder
                </button>
              </div>
            ) : null}

            <div
              className={cn('flex min-h-[70px] items-center justify-between gap-4 px-5 py-4', {
                'border-steel-75 border-t': canApply,
              })}
            >
              <div className="flex min-w-0 items-center gap-4">
                <FolderArchive aria-hidden="true" className="text-steel-400 size-6 shrink-0" />
                <div className="min-w-0">
                  <p className="text-steel-500 text-[14px] leading-5 font-semibold">Download .zip</p>
                  <p className="text-steel-400 mt-1 text-[12px] leading-4">
                    A copy in your downloads folder that you unpack and file yourself.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onDownload}
                disabled={!onDownload || isDownloading}
                className={cn(
                  'text-brand-400 flex min-w-[122px] shrink-0 items-center justify-center gap-2',
                  'hover:text-brand-300 text-[14px] leading-5 font-semibold transition-colors',
                  'disabled:text-steel-200 disabled:cursor-not-allowed',
                )}
                aria-label={`Download ${folderName}`}
              >
                {isDownloading ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                Download .zip
              </button>
            </div>
          </div>
        </div>
      </div>
      <FolderApplyDialog
        folderName={folderName}
        isApplying={isApplying}
        onCancel={onCancelApply}
        onConfirm={onConfirmApply}
        plan={applyPlan ?? null}
      />
    </div>
  );
}
